// src/components/hub/work/sections/WorkOverlays.js
import React from "react";
import { ClientSlideOut, DrillSheet } from "../../../hub/kit";
import { assignWork, bulkWork } from "../../../../api/hub/work";
import AssignmentPopover from "../overlays/AssignmentPopover";
import MovePickerModal from "../overlays/MovePickerModal";
import DeleteConfirmModal from "../overlays/DeleteConfirmModal";
import CreateTaskModal from "../overlays/CreateTaskModal";
import SavePresetModal from "../overlays/SavePresetModal";
import RepeatTaskModal from "../overlays/RepeatTaskModal";
import UndoToast from "../overlays/UndoToast";

// Every overlay the work page renders — both assignment popovers (with their
// async onSelect glue bodies), both move pickers, the delete/archive confirm,
// the client slide-out, the drill sheet, the create/preset modals, and the
// undo toast. TaskSlideOut deliberately stays in the page (it is the page's
// primary surface). Pure pass-through: all state and handlers are owned by
// WorkPage / its hooks and passed in.
const WorkOverlays = ({
  // target / open states
  assignTarget,
  rowAssignTarget,
  moveTarget,
  rowMoveTarget,
  deleteTarget,
  archiveTarget,
  slideOutClientId,
  drillState,
  showCreateModal,
  showSaveModal,
  undoToast,
  // close / confirm callbacks
  setAssignTarget,
  setRowAssignTarget,
  setMoveTarget,
  setRowMoveTarget,
  setDeleteTarget,
  setArchiveTarget,
  setSlideOutClientId,
  setShowCreateModal,
  setShowSaveModal,
  closeDrill,
  confirmBulkDelete,
  confirmBulkArchive,
  handleBulkMoveConfirm,
  handleRowMoveConfirmFinal,
  handleRestore,
  // modal inputs
  workTypeOptions,
  allFilterOptions,
  existingTags,
  handleCreateTask,
  handleSavePreset,
  // repeating-task modal (page-level open state + current task). The Repeat…
  // button in WorkPanel calls onRepeat to open this; on success handleRepeatSet
  // refreshes the task and the modal closes via setShowRepeatModal(false).
  showRepeatModal,
  repeatTask,
  setShowRepeatModal,
  handleRepeatSet,
  // popover glue
  selectedTaskId,
  setDetail,
  patchItem,
  clearSelection,
  getOwnerName,
  setRetryNonce,
  toastError,
  toastSuccess,
}) => (
  <>
    {/* Assignment popover (bulk / slide-out) */}
    {assignTarget && (
      <AssignmentPopover
        clientId={assignTarget.clientId}
        workType={assignTarget.workType}
        teamMembers={allFilterOptions?.owners || []}
        onSelect={async (ownerId) => {
          try {
            let patchIds = [];
            if (assignTarget.bulkIds) {
              const resp = await bulkWork(assignTarget.bulkIds, "assign", ownerId);
              if (resp?.data?.errors?.length > 0) {
                toastError(`Assign failed: Failed for ${resp.data.errors.length} item(s): ${resp.data.errors.map(e => e.message || e).join(", ")}`, null, "assign");
              }
              clearSelection();
              patchIds = assignTarget.bulkIds;
            } else {
              await assignWork(selectedTaskId, ownerId);
              patchIds = [selectedTaskId];
            }
            patchIds.forEach((id) => patchItem(id, { owner_id: ownerId, owner: getOwnerName(ownerId) }));
            setRetryNonce((n) => n + 1);
          } catch (e) { console.error("Assign failed:", e); }
          setAssignTarget(null);
        }}
        onClose={() => setAssignTarget(null)}
      />
    )}

    {/* Move picker (bulk) */}
    <MovePickerModal
      open={!!moveTarget}
      taskIds={moveTarget || []}
      onSelect={handleBulkMoveConfirm}
      onClose={() => setMoveTarget(null)}
    />

    {/* Assignment popover (row-level) */}
    {rowAssignTarget && (
      <AssignmentPopover
        clientId={rowAssignTarget.clientId}
        workType={rowAssignTarget.workType}
        taskId={rowAssignTarget.taskId}
        taskTitle={rowAssignTarget.taskTitle}
        teamMembers={allFilterOptions?.owners || []}
        onSelect={async (ownerId) => {
          try {
            await assignWork(rowAssignTarget.taskId, ownerId);
            patchItem(rowAssignTarget.taskId, { owner_id: ownerId, owner: getOwnerName(ownerId) });
            // Update the slide-out header instantly when the assignment is for
            // the currently-open task (no refetch → no skeleton flash). The
            // list row refreshes via retryNonce → useWorkBucketCache below.
            if (rowAssignTarget.taskId === selectedTaskId) {
              setDetail((prev) => prev?.data
                ? { ...prev, data: { ...prev.data, task: { ...prev.data.task, owner_id: ownerId, owner: getOwnerName(ownerId) } } }
                : prev);
            }
            setRetryNonce((n) => n + 1);
          } catch (e) { toastError(`Assign failed: ${e?.message || "Unknown"}`, rowAssignTarget.taskId, "assign"); }
          setRowAssignTarget(null);
        }}
        onClose={() => setRowAssignTarget(null)}
      />
    )}

    {/* Move picker (row-level) */}
    <MovePickerModal
      open={!!rowMoveTarget}
      taskIds={rowMoveTarget ? [rowMoveTarget.taskId] : []}
      onSelect={handleRowMoveConfirmFinal}
      onClose={() => setRowMoveTarget(null)}
    />

    {/* Delete confirmation */}
    <DeleteConfirmModal
      open={!!deleteTarget}
      ids={deleteTarget?.ids || []}
      archiveFallbackIds={archiveTarget}
      onConfirmDelete={confirmBulkDelete}
      onConfirmArchive={confirmBulkArchive}
      onClose={() => { setDeleteTarget(null); setArchiveTarget(null); }}
    />

    {/* Client slide-out */}
    <ClientSlideOut clientId={slideOutClientId} onClose={() => setSlideOutClientId(null)} />

    {/* Drill sheet */}
    <DrillSheet
      open={!!drillState}
      what={drillState?.what}
      id={drillState?.id}
      scope={drillState?.scope}
      onClose={closeDrill}
    />

    {/* Modals */}
    <CreateTaskModal
      open={showCreateModal}
      onClose={() => setShowCreateModal(false)}
      onCreate={handleCreateTask}
      workTypeOptions={workTypeOptions}
      allFilterOptions={allFilterOptions}
      existingTags={existingTags}
    />
    <SavePresetModal
      open={showSaveModal}
      onClose={() => setShowSaveModal(false)}
      onSave={handleSavePreset}
    />

    {/* Repeat-task modal — promotes the current task into a recurring rule
        via fn_recurring_from_task. Open state + task are owned by WorkPage. */}
    <RepeatTaskModal
      open={!!showRepeatModal}
      onClose={() => setShowRepeatModal(false)}
      task={repeatTask}
      onRepeatSet={handleRepeatSet}
    />

    {/* Toasts */}
    <UndoToast
      count={undoToast?.visible ? undoToast.ids.length : 0}
      onRestore={() => handleRestore(undoToast?.ids || [])}
    />
  </>
);

export default WorkOverlays;
